import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import { VideoCallScreen } from '@/features/video/components/VideoCallScreen';
import { isStaffCallPopupTab } from '@/features/video/lib/openSessionCallTab';
import { requirePermission } from '@/lib/guards';

export const Route = createFileRoute('/_staff-fullscreen/app/sessions/$sessionId/call')({
  beforeLoad: () => {
    requirePermission('session:read');
  },
  component: VideoCallRoute,
  staticData: { crumb: 'Call' },
});

export function VideoCallRoute() {
  const { sessionId } = useParams({
    from: '/_staff-fullscreen/app/sessions/$sessionId/call',
  });
  const navigate = useNavigate();

  // When the route was opened in a new tab from a workflow surface, exit
  // by closing the tab — the user's original tab still holds their
  // session list / drawer context. Direct navigation (typed URL, refresh)
  // falls back to in-tab routing.
  const exitOrClose = (fallback: () => void) => {
    if (isStaffCallPopupTab()) {
      window.close();
      // window.close() is asynchronous in some browsers and may be
      // silently denied. Run the fallback so we don't strand the user
      // on a frozen call screen if the close attempt is rejected.
    }
    fallback();
  };

  return (
    <VideoCallScreen
      sessionId={sessionId}
      onExit={() => {
        exitOrClose(() => {
          void navigate({
            to: '/app/sessions/$sessionId',
            params: { sessionId },
          });
        });
      }}
      onPostSessionRedirect={() => {
        exitOrClose(() => {
          void navigate({
            to: '/app/sessions',
            search: {
              panel: 'session' as const,
              id: sessionId,
              drawerTab: 'notes' as const,
              segment: 'all' as const,
            },
          });
        });
      }}
    />
  );
}
